import { EventEmitter } from '@angular/core';
import { JSONSchema, RelationalOperator } from '../types/json-schema';
import { FormComponent } from '../form.component';
import { Validator, FormProperty } from '../angular2-schema-form-shim';
import { SchemaErrorDetailSparse } from '../types/z-schema';
import { SchemaErrorDetail } from 'z-schema';
import { SPSchemaValidatorFactory } from '../schemavalidatorfactory';
import { DATE_DAY } from '../constants';

export interface ModuleHasBmi {
  body_weight?: number;
  body_height?: number;
  bmi?: number;
  bmi_compute?: boolean;
}

export interface ModuleHasProms {
  proms?: {
    id?: string;
    message?: string;
    follow_up_7?: boolean;
    follow_up_30_and_180?: boolean;
    patient_name?: string;
    patient_phone?: string;
  };
}

export abstract class ModuleValue {
  id?: number;
  parent_id?: number;
  date_time?: string;
  incomplete?: boolean;
  draft?: boolean;
  user_id?: number;
  location?: number;
  patient_identifier?: string;
  date_of_birth?: string;
};

type RelationMessageType = 'number' | 'date';

export abstract class SchemaModule<T extends ModuleValue = ModuleValue> {
  schema: JSONSchema<T>;
  form: FormComponent;
  updater: EventEmitter<{ values: any }> = new EventEmitter();
  rebuild: EventEmitter<void> = new EventEmitter();
  validators: {[ path in keyof T ]?: Validator[]} = {};

  constructor() {
  }

  ngOnInit() {
    this.setupValidators();
  }

  /**
   * Called when parent form has finished loading. Used to set up custom value listeners.
   * @param form Parent form component
   */
  hook( form: FormComponent ) {
    this.form = form;

    if ( !this.schemaUsesOrderByProcedure() ) return;

    const mpProp = this.form.rootProperty.searchProperty( '/main_procedure' );
    if ( !mpProp ) return;

    mpProp.valueChanges.subscribe( ( value: string ) => {
      if ( !value ) return;
      this.reorderForProcedure( value );
    } );
  }

  loadSchema( schema: JSONSchema<T> ) {
    // Create a copy to prevent breaking the original
    this.schema = JSON.parse( JSON.stringify( schema ) ) as JSONSchema<T>;
    this.applyOrderByProcedureToOrder( this.resolveInitialProcedure() );
  }

  /**
   * Initial pass, before the SchemaPreprocessor runs: wherever a node has
   * `orderByProcedure`, reorder `node.order` using the procedure's list. Fields
   * not in that list are appended, preserving the flat order as fallback (so
   * the preprocessor's `checkFieldsUsage` still sees every property).
   */
  private applyOrderByProcedureToOrder( procedure: string | undefined, node: any = this.schema ) {
    if ( !node || typeof node !== 'object' ) return;
    const obp = node.orderByProcedure as { [ p: string ]: string[] } | undefined;
    if ( obp && Array.isArray( node.order ) ) {
      const base = this.pickProcedureList( obp, procedure );
      if ( base ) node.order = this.mergeOrder( base, node.order );
    }
    if ( node.properties ) {
      for ( const k of Object.keys( node.properties ) ) {
        this.applyOrderByProcedureToOrder( procedure, node.properties[ k ] );
      }
    }
    if ( node.items && !Array.isArray( node.items ) ) {
      this.applyOrderByProcedureToOrder( procedure, node.items );
    }
  }

  /**
   * Runtime pass, after the preprocessor consumed `order`: mutate
   * `fieldsets[0].fields` in place for each node with `orderByProcedure`.
   * Template uses `*ngFor` over fieldsets.fields with `getProperty(fieldId)`
   * so a new array reference triggers re-iteration while reusing existing
   * FormProperty instances — form state is preserved.
   */
  private applyOrderByProcedureToFieldsets( procedure: string, node: any = this.schema ) {
    if ( !node || typeof node !== 'object' ) return;
    const obp = node.orderByProcedure as { [ p: string ]: string[] } | undefined;
    if ( obp && Array.isArray( node.fieldsets ) && node.fieldsets[ 0 ] && Array.isArray( node.fieldsets[ 0 ].fields ) ) {
      const base = this.pickProcedureList( obp, procedure );
      if ( base ) {
        // Merge against the *current* fieldset list — which preserves any Vue
        // shell pruning (e.g. `proms` removed at AppCaseSidebar.vue:350) and
        // covers procedure-specific fields the pick list may omit.
        node.fieldsets[ 0 ].fields = this.mergeOrder( base, node.fieldsets[ 0 ].fields );
      }
    }
    if ( node.properties ) {
      for ( const k of Object.keys( node.properties ) ) {
        this.applyOrderByProcedureToFieldsets( procedure, node.properties[ k ] );
      }
    }
    if ( node.items && !Array.isArray( node.items ) ) {
      this.applyOrderByProcedureToFieldsets( procedure, node.items );
    }
  }

  private pickProcedureList( obp: { [ p: string ]: string[] }, procedure: string | undefined ): string[] | null {
    const base = ( procedure && obp[ procedure ] ) || obp._default || Object.values( obp )[ 0 ];
    return Array.isArray( base ) ? base : null;
  }

  /**
   * Reorder `existing` to match `base` where the two overlap, then append any
   * remaining `existing` entries. Drops names in `base` that aren't in
   * `existing` — they'd be invented fields the form doesn't know about.
   */
  private mergeOrder( base: string[], existing: string[] ): string[] {
    const existingSet = new Set<string>( existing );
    const seen = new Set<string>();
    const result: string[] = [];
    for ( const name of base ) {
      if ( existingSet.has( name ) && !seen.has( name ) ) {
        result.push( name );
        seen.add( name );
      }
    }
    for ( const name of existing ) {
      if ( !seen.has( name ) ) {
        result.push( name );
        seen.add( name );
      }
    }
    return result;
  }

  private resolveInitialProcedure(): string | undefined {
    const mp: any = ( this.schema.properties as any ) && ( this.schema.properties as any ).main_procedure;
    return mp && typeof mp.default === 'string' ? mp.default : undefined;
  }

  private schemaUsesOrderByProcedure( node: any = this.schema ): boolean {
    if ( !node || typeof node !== 'object' ) return false;
    if ( node.orderByProcedure ) return true;
    if ( node.properties ) {
      for ( const k of Object.keys( node.properties ) ) {
        if ( this.schemaUsesOrderByProcedure( node.properties[ k ] ) ) return true;
      }
    }
    if ( node.items && !Array.isArray( node.items ) ) {
      return this.schemaUsesOrderByProcedure( node.items );
    }
    return false;
  }

  private reorderForProcedure( procedure: string ) {
    this.applyOrderByProcedureToFieldsets( procedure );
    this.rebuild.emit();
  }

  loadLocations( locations: { [ key: number ]: string } ) {
    let locationSchema: JSONSchema = this.schema.properties[ 'location' ];
    locationSchema.enum = Object.keys( locations ).map( Number );
    locationSchema.options.enum_titles = Object.values( locations );
    this.setupValidators();
  }

  getProperty<ModuleValueKey extends keyof T>( propertyPath: ModuleValueKey ) {
    return this.form.rootProperty.searchProperty( `/${propertyPath}` );
  }

  listenValue<ModuleValueKey extends keyof T>( propertyPaths: ModuleValueKey | ModuleValueKey[], listener: ( ...value: any[] ) => void ) {
    let propertyPathsArray = [].concat( propertyPaths );

    propertyPathsArray.forEach( ( propertyPath, propertyIndex ) => {
      this.getProperty( propertyPath ).valueChanges.subscribe( ( value ) => {
        let values = propertyPathsArray.map( ( p, i ) => i === propertyIndex ? value : this.getValue( p, true ) );
        listener( ...values );
      } );
    } );
  }

  getValue<ModuleValueKey extends keyof T>( propertyPath: ModuleValueKey, checkVisibility = false ): T[ ModuleValueKey ] {
    let property = this.getProperty( propertyPath );
    if ( checkVisibility && !property.visible ) return undefined;
    return property.value;
  }

  updateValue<ModuleValueKey extends keyof T>( propertyPath: ModuleValueKey, newValue: T[ ModuleValueKey ] ) {
    if ( newValue !== this.getValue( propertyPath ) ) {
      this.getProperty( propertyPath ).setValue( newValue, false );
    }
  }

  listenVisibility<ModuleValueKey extends keyof T>( propertyPath: ModuleValueKey, listener: ( value: any ) => void ) {
    this.getProperty( propertyPath ).visibilityChanges.subscribe( listener );
  }

  /**
   * Difference between two date strings expressed in months. Returns `null` if
   * either date is missing. `divisor` is the average days-per-month used to
   * convert the day difference (e.g. 30.5 for follow-up duration).
   */
  diffInMonths( a: string, b: string, divisor: number ): number | null {
    if ( !a || !b ) return null;
    const diffMs = new Date( a ).getTime() - new Date( b ).getTime();
    const diffDays = diffMs / ( 1000 * 60 * 60 * 24 );
    return Math.round( ( diffDays / divisor ) * 10 ) / 10;
  }

  /**
   * Helper function to compare two values using the provided operator.
   * @param value1 First value
   * @param value2 Second value
   * @param operator Comparison operator
   */
  relation<U>( value1: U, value2: U, operator: RelationalOperator ) {
    let operatorFunctions: {[ operator in RelationalOperator ]: ( a: U, b: U ) => boolean } = {
      '==': ( a, b ) => a == b,
      '!=': ( a, b ) => a != b,
      '>': ( a, b ) => a > b,
      '>=': ( a, b ) => a >= b,
      '<': ( a, b ) => a < b,
      '<=': ( a, b ) => a <= b,
    };

    return operatorFunctions[ operator ]( value1, value2 );
  }

  /**
   * Generate error messages for value comparison validators
   * @param property1 First Property
   * @param property2 Second property
   * @param operator Comparison operator
   * @param valueType Type of value being compared
   */
  relationMessage<K extends keyof T>( property1: K, property2: K, operator: RelationalOperator, valueType: RelationMessageType = 'number' ) {
    let relationalOperatorMessages: {[ valueType in RelationMessageType ]: {[ operator in RelationalOperator ]: string } } = {
      'number': {
        '==': '%0 must be equal to %1.',
        '!=': '%0 must not be equal to %1.',
        '>': '%0 must be greater than %1.',
        '>=': '%0 must be greater than or equal to %1.',
        '<': '%0 must be less than %1.',
        '<=': '%0 must be less than or equal to %1.',
      },
      'date': {
        '==': '%0 must be equal to %1.',
        '!=': '%0 must not be equal to %1.',
        '>': '%0 must be later than %1.',
        '>=': '%0 must be later than or equal to %1.',
        '<': '%0 must be earlier than %1.',
        '<=': '%0 must be earlier than or equal to %1.',
      },
    };
    return this.generateMessage( relationalOperatorMessages[ valueType ][ operator ], property1, property2 );
  }

  /**
   * Create a message by injecting property titles into placeholders.
   *
   * @param message Message with placeholders. A placeholder is a percent signs followed by a number.
   * This number is the index of the property parameter starting at zero. For example: `%0` or `%13`
   * @param properties The name of each property to be injected into the message.
   * @returns string The compiled message
   * @example
   * generateMessage( 'The %0 field requires %1 to be selected', 'first_name', 'generate_user' )
   * // 'The First Name field requires Generate User to be selected'
   */
  generateMessage<K extends keyof T>( message: string, ...properties: K[] ) {
    return message.replace( /%\d+/g, index => {
      return ( <JSONSchema>this.getProperty( properties[ parseInt( index.substr( 1 ) ) ] ).schema ).title;
    } );
  }

  relationalOperatorConverse( operator: RelationalOperator ): RelationalOperator {
    let converseOperators: {[ operator in RelationalOperator ]: RelationalOperator} = {
      '==': '==',
      '!=': '!=',
      '>': '<',
      '>=': '<=',
      '<': '>',
      '<=': '>=',
    };
    return converseOperators[ operator ];
  }

  /**
   * Generate a validator for array-type properties that are required to have at least one value.
   * @param property Field property to validate
   */
  validatorRequiredArray<K extends keyof T>( property: K ) {
    this.addValidator( property, ( value: string[], formProperty, form ): SchemaErrorDetailSparse[] => {
      if ( !formProperty.visible ) return [];
      if ( value.length === 0 ) {
        return [ {
          code: 'PROPERTY_VALUE_REQUIRED',
          path: `#/${property}`,
          message: `Missing required property: ${property}`,
          params: [ property ],
        }];
      }
      return [];
    } );
  }

  /**
   * Add validators to compare the values of two properties
   * @param property1 First property
   * @param property2 Second property
   * @param operator Comparison operator
   * @param valueTranslator Function to convert values
   * @param message Override error message
   * @param valueType Type of value being compared. Used to generate message.
   * @param setupTwin Attach validator to second property. Default `true`
   */
  validatorValueComparison<K extends keyof T>(
    property1: K,
    property2: K,
    operator: RelationalOperator,
    valueTranslator?: ( value: T[ K ] ) => any,
    message?: string,
    valueType?: RelationMessageType,
    setupTwin: boolean = true,
  ) {
    let twinSubscribed = false;
    this.addValidator( property1, ( value1: T[ K ], formProperty, form ) => {
      let value2 = this.getValue( property2 );
      let translatedValue1 = valueTranslator ? valueTranslator( value1 ) : value1;
      let translatedValue2 = valueTranslator ? valueTranslator( value2 ) : value2;
      let code1 = `validatorValueComparison|${property1}|${property2}`;
      let code2 = `validatorValueComparison|${property2}|${property1}`;
      let property1Obj = <FormProperty>form.getProperty( property1 );
      let property2Obj = <FormProperty>form.getProperty( property2 );

      let propertyMissing = ( propertyObj: FormProperty, value: T[ K ] ) => {
        return ( <SPSchemaValidatorFactory>propertyObj.schemaValidatorFactory ).isPropertyMissing( value, propertyObj.schema );
      };

      let clearTwinErrors = () => {
        property2Obj.setErrors( property2Obj.filterErrors( ( error: SchemaErrorDetail ) => error.code !== code2 ) );
      };

      if ( !twinSubscribed ) {
        twinSubscribed = true;
        property1Obj.valueChanges.subscribe( value => {
          if ( propertyMissing( property1Obj, value ) ) {
            clearTwinErrors();
          }
        } );
      }

      // Don't validate if...
      if (
        // ... either of the fields are empty
        ( propertyMissing( property1Obj, value1 ) || propertyMissing( property2Obj, value2 ) ) ||
        // ... either of the fields are hidden
        ( !property1Obj.visible || !property2Obj.visible )
      ) {
        clearTwinErrors();
        return [];
      }

      if ( !this.relation( translatedValue1, translatedValue2, operator ) ) {
        // Also add the error to the twin property
        property2Obj.extendErrors( [ {
          code: code2,
          path: `#/${property2}`,
          message: message || this.relationMessage( property2, property1, this.relationalOperatorConverse( operator ), valueType ),
        }] );
        return [ {
          code: code1,
          path: `#/${property1}`,
          message: message || this.relationMessage( property1, property2, operator, valueType ),
        }];
      } else {
        clearTwinErrors();
        return [];
      }
    } );

    // Don't run this if already in the 'twin' validator
    if ( setupTwin ) {
      this.validatorValueComparison( property2, property1, this.relationalOperatorConverse( operator ), valueTranslator, message, valueType, false );
    }
  }

  /**
   * Add validators to compare two date properties
   * @param dateProperty1 First date property
   * @param dateProperty2 Second date property
   * @param operator Comparison operator
   * @param message Override error message
   */
  validatorDateComparison<K extends keyof T>(
    dateProperty1: K,
    dateProperty2: K,
    operator: RelationalOperator,
    message?: string,
  ) {
    this.validatorValueComparison(
      dateProperty1,
      dateProperty2,
      operator,
      v => new Date( v as any ),
      null,
      'date',
    );
  }

  /**
   * Add validators to compare two date properties
   * @param dateProperty1 First date property
   * @param dateValue instance of Date class
   * @param operator Comparison operator
   * @param valueTranslator
   * @param message Override error message
   */
  validatorDate<K extends keyof T>(
    dateProperty1: K,
    dateValue: Date,
    operator: RelationalOperator,
    valueTranslator?: ( value: T[ K ] ) => any,
    message?: string,
  ) {
    // let valueTranslator = v => new Date( v as any );

    this.addValidator( dateProperty1, ( value1: T[ K ], formProperty, form ) => {
      let translatedValue1 = valueTranslator ? valueTranslator( value1 ) : value1;
      let code1 = `validatorValueComparison|${dateProperty1}|`;
      let property1Obj = <FormProperty>form.getProperty( dateProperty1 );

      let propertyMissing = ( propertyObj: FormProperty, value: T[ K ] ) => {
        return ( <SPSchemaValidatorFactory>propertyObj.schemaValidatorFactory ).isPropertyMissing( value, propertyObj.schema );
      };

      // Don't validate if...
      if (
        // ... either of the fields are empty
        ( propertyMissing( property1Obj, value1 ) ) ||
        // ... either of the fields are hidden
        ( !property1Obj.visible )
      ) {
        return [];
      }

      if ( !this.relation( translatedValue1, dateValue, operator ) ) {
        let operatorMessages = {
          '==': '%0 must be equal to %1.',
          '!=': '%0 must not be equal to %1.',
          '>': '%0 must be later than %1.',
          '>=': '%0 must be later than or equal to %1.',
          '<': '%0 must be earlier than %1.',
          '<=': '%0 must be earlier than or equal to %1.',
        };

        if (!message) {
          message = operatorMessages[ operator ]
            .replace( '%0', ( this.getProperty( dateProperty1 ).schema ).title)
            .replace( '%1', dateValue.toISOString().split( 'T' )[ 0 ]);
        }

        // Also add the error to the twin property
        return [ {
          code: code1,
          path: `#/${dateProperty1}`,
          message: message,
        }];
      } else {
        return [];
      }
    } );
  }

  /**
   * Attach a validator function to a property
   * @param property Property name
   * @param validator Validator function
   */
  addValidator<K extends keyof T>( property: K, validator: Validator ) {
    if ( typeof this.validators[ `/${property}` ] === 'undefined' ) {
      this.validators[ `/${property}` ] = [];
    }
    this.validators[ `/${property}` ].push( validator );
  }

  setupValidators() {

    // Automatically apply the custom validator for array properties if needed
    if ( this.schema && typeof this.schema.required !== 'boolean' ) {
      for ( let property of this.schema.required ) {
        if ( this.schema.properties[ property ].type === 'array' ) {
          this.validatorRequiredArray( <any>property );
        }
      }
    }
  }
}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/forms/module.ts